import React, { useState, useEffect } from 'react';
import { toast } from '../hooks/use-toast';
import SearchFilters from '../components/SearchFilters';
import SavedSearches from '../components/SavedSearches';
import SearchDetails from '../components/SearchDetails';
import SettingsPage from './SettingsPage';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Search,
  Plus,
  LogOut,
  User,
  Home,
  History,
  Settings,
  Menu,
  X,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AutoFetchProvider } from '../contexts/AutoFetchContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const HomePage = () => {
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'new-search', 'search-details', 'settings'
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [backendVersion, setBackendVersion] = useState('');
  const { user, logout } = useAuth();

  // Fetch backend version on component mount
  useEffect(() => {
    const fetchBackendVersion = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/version`);
        if (response.ok) {
          const data = await response.json();
          setBackendVersion(data.version);
        }
      } catch (error) {
        console.error('Error fetching backend version:', error);
        setBackendVersion('Unknown');
      }
    };

    fetchBackendVersion();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Déconnexion réussie",
        description: "Vous avez été déconnecté avec succès.",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la déconnexion.",
        variant: "destructive",
      });
    }
  };

  const handleUrlSubmit = async (url, searchName) => {
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/scrape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url,
          user_id: user.uid,
          search_name: searchName
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast({
            title: "Succès!",
            description: `${data.listings?.length || 0} annonces extraites avec succès.`,
          });
          // Go back to dashboard to see the new search
          setCurrentView('dashboard');
        } else {
          throw new Error(data.message || 'Erreur lors de l\'extraction');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail?.message || 'Erreur lors de l\'extraction');
      }
    } catch (error) {
      console.error('Error scraping listings:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'extraire les annonces. Veuillez vérifier l'URL.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshSearch = async (searchId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/searches/${searchId}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast({
            title: "Actualisation terminée",
            description: `${data.new_listings || 0} nouvelle${data.new_listings > 1 ? 's' : ''} annonce${data.new_listings > 1 ? 's' : ''} trouvée${data.new_listings > 1 ? 's' : ''}.`,
          });
        }
      } else {
        throw new Error('Erreur lors de l\'actualisation');
      }
    } catch (error) {
      console.error('Error refreshing search:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'actualiser la recherche.",
        variant: "destructive",
      });
    }
  };

  const handleSearchSelect = (search) => {
    setSelectedSearch(search);
    setCurrentView('search-details');
    setSidebarOpen(false); // Close sidebar on mobile
  };

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Mes Recherches',
      icon: Home,
      active: currentView === 'dashboard'
    },
    {
      id: 'new-search',
      label: 'Nouvelle Recherche',
      icon: Plus,
      active: currentView === 'new-search'
    },
    {
      id: 'settings',
      label: 'Paramètres',
      icon: Settings,
      active: currentView === 'settings'
    }
  ];

  return (
    <AutoFetchProvider onRefreshSearch={handleRefreshSearch}>
      <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 lg:z-0
        w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">L</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Listing Tracker</h1>
                <p className="text-xs text-gray-600">Suivi multi-sites d'annonces</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentView(item.id);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors
                  ${item.active
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:bg-gray-50'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <div className="text-xs text-gray-500 px-3 pb-1">Sites supportés:</div>
            <a
              href="https://www.avito.ma"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <ExternalLink className="w-5 h-5" />
              <span className="text-sm">Avito.ma</span>
            </a>
            <a
              href="https://www.moteur.ma"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <ExternalLink className="w-5 h-5" />
              <span className="text-sm">Moteur.ma</span>
            </a>

            <div className="border-t border-gray-200 mt-2 pt-2">
              <div className="text-xs text-gray-500 px-3 pb-1">Version Backend: {backendVersion || 'Loading...'}</div>
            </div>

            <div className="flex items-center space-x-3 px-3 py-2 text-gray-600">
              <User className="w-5 h-5" />
              <span className="text-sm truncate">{user?.email}</span>
            </div>

            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-gray-600 hover:text-gray-800"
            >
              <LogOut className="w-5 h-5 mr-3" />
              <span>Déconnexion</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-4 lg:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden"
              >
                <Menu className="w-5 h-5" />
              </Button>

              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {currentView === 'dashboard' && 'Tableau de bord'}
                  {currentView === 'new-search' && 'Nouvelle recherche'}
                  {currentView === 'search-details' && selectedSearch?.name}
                  {currentView === 'settings' && 'Paramètres'}
                </h2>
                <p className="text-sm text-gray-600">
                  {currentView === 'dashboard' && 'Gérez vos recherches sauvegardées'}
                  {currentView === 'new-search' && 'Créez une nouvelle recherche d\'annonces'}
                  {currentView === 'search-details' && 'Détails de la recherche'}
                  {currentView === 'settings' && 'Configurez vos préférences d\'actualisation automatique'}
                </p>
              </div>
            </div>

            {/* Mobile close button when sidebar is open */}
            {sidebarOpen && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-4 lg:p-6">
          {currentView === 'dashboard' && (
            <SavedSearches
              onSearchSelect={handleSearchSelect}
              onRefreshSearch={handleRefreshSearch}
            />
          )}

          {currentView === 'new-search' && (
            <Card>
              <CardContent className="p-6">
                <SearchFilters
                  onUrlSubmit={handleUrlSubmit}
                  isLoading={isLoading}
                  showSearchName={true}
                />
              </CardContent>
            </Card>
          )}

          {currentView === 'search-details' && selectedSearch && (
            <SearchDetails
              search={selectedSearch}
              onBack={() => setCurrentView('dashboard')}
              onRefreshSearch={handleRefreshSearch}
            />
          )}

          {currentView === 'settings' && (
            <SettingsPage
              onBack={() => setCurrentView('dashboard')}
            />
          )}
        </div>
      </div>
      </div>
    </AutoFetchProvider>
  );
};

export default HomePage;